import { useId, useState } from 'react';
import {
  Box,
  Button,
  CloseIcon,
  Dialog,
  IconButton,
  SearchIcon,
  Stack,
  Text,
  TextField,
  WarningIcon,
} from '@citibox-panel/ui';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { usePhoneSearch } from '../../phone-search/hooks/usePhoneSearch';
import { recipientEligibility, type RecipientEligibility } from '../lib/recipientEligibility';

// TODO: i18n — copy en ES hasta Fase 1.
const COPY = {
  title: 'Cambiar titular',
  close: 'Cerrar',
  phoneLabel: 'Teléfono',
  // The resolver requires a leading «+» and runs no search without one, so the field says so
  // before the agent types a valid national number and nothing happens.
  phonePlaceholder: '+34…',
  search: 'Buscar',
  nameLabel: 'Nombre',
  cancel: 'Cancelar',
  save: 'Guardar',
  // Sized to the delivery and never a fixed line, so it does not become the sentence an agent
  // learns to skip: it names the COUNT, and it is absent entirely when there is nobody to retire.
  authorizedWarning: (count: number) =>
    count === 1
      ? 'Se retirará 1 autorizado a recoger.'
      : `Se retirarán los ${String(count)} autorizados a recoger.`,
  // Always, whatever the count. That nobody is told is the one thing the agent cannot discover any
  // other way, and it is the whole reason the panel lands them on the new titular afterwards.
  noNotice: 'Nadie recibe aviso.',
  // A failed READ, which is not a refusal: nothing was decided about the account, so the sentence
  // says the search failed and the magnifier stays live.
  readFailed: 'No se pudo buscar el teléfono. Inténtalo de nuevo.',
  // `detectPhone` demands a leading «+», so nine valid-looking digits resolve to nothing at all.
  // Without this the agent types a number, presses the magnifier and watches nothing happen. Same
  // hint the header carries, for the same reason.
  missingPrefix: 'Escribe el teléfono con prefijo, por ejemplo +34…',
};

// Why an account cannot take the parcel, one sentence per cause. The distinction is made HERE and
// not from the backend's answer: three of these four arrive on the wire as a single
// `delivery_recipient_not_eligible`, so a code cannot tell the agent which one it was, while the
// resolved account's own `active` and `status` can. The backend's safety-net wording for the same
// facts lives in `changeRecipientError` and is deliberately NOT shared with this: that one explains
// a write that FAILED and is keyed by wire code, this one explains a field that was REFUSED and is
// keyed by cause — and the two vocabularies are not one-to-one.
// TODO: i18n — copy en ES hasta Fase 1.
const REFUSAL: Record<Exclude<RecipientEligibility, { eligible: true }>['reason'], string> = {
  // Wording from the delivered design (capture 4), with a full stop added so the four sentences
  // that share this slot are punctuated alike.
  'not-found': 'No existe usuario con este teléfono.',
  blocked: 'No se puede reasignar a una cuenta bloqueada.',
  anonymized: 'Esa cuenta está dada de baja.',
  'already-titular': 'Esa cuenta ya es la titular de esta entrega.',
};

export interface ChangeRecipientDialogProps {
  open: boolean;
  onClose: () => void;
  // The chosen account, with its id already a number: the write's contract asks for one and a
  // search hit carries a string, so the conversion happens once, where the choice is made.
  onSave: (person: { id: number; name: string; phone: string }) => void;
  searchUsersByPhone: (phone: string) => Promise<UserSearchHit[]>;
  // Needed to tell the agent «that account already holds this parcel» BEFORE the write — the one
  // refusal that is about this «Entrega» and not about the account.
  currentRecipientId: number | null;
  // How many authorized pickups the handover will retire. Drives the warning, which is here rather
  // than behind a confirm step because «Guardar» IS the confirmation now.
  authorizedCount: number;
  // The write is in flight. «Guardar» goes busy and stops accepting presses — it cannot be taken
  // back, so a second one must not start a second.
  saving?: boolean;
}

// The dialog that CHOOSES the new titular. It does not write: it resolves a phone to a «Usuario»,
// says whether that account may hold the parcel, and hands the choice back. Confirming happens
// outside, in the «Acciones» card's standard confirm step, which is where the agent is warned that
// the authorized pickups go with it (PM, 2026-09-14).
//
// Resolution is SEARCH-ON-SUBMIT and reuses the header's own `usePhoneSearch`: same hook, same
// `detectPhone`, and — because the hook keys its query by the normalized number — the same cache
// the agent's header search just filled. A resolver of its own here could disagree with the one
// they used two fields up.
export function ChangeRecipientDialog({
  open,
  onClose,
  onSave,
  searchUsersByPhone,
  currentRecipientId,
  authorizedCount,
  saving = false,
}: ChangeRecipientDialogProps) {
  const titleId = useId();
  const [phone, setPhone] = useState('');
  // The verdict on what the LAST resolution found, or null before there has been one. Not derived
  // from the hook's `hits`: those stay behind after a failure, and a stale verdict on screen is
  // worse than none.
  const [eligibility, setEligibility] = useState<RecipientEligibility | null>(null);
  // The three things that can be said under the field, and only ever one at a time: the read
  // failed, the input is not a phone yet, or the resolved account was refused. Kept apart from
  // `eligibility` because neither of the first two is a verdict ABOUT an account.
  const [readFailed, setReadFailed] = useState(false);
  const [missingPrefix, setMissingPrefix] = useState(false);
  const { submit, status } = usePhoneSearch(searchUsersByPhone);

  const resolve = async () => {
    setReadFailed(false);
    try {
      // `submit` returns null WITHOUT searching when the input is not a phone — the same judge the
      // header uses, so the two cannot disagree about what a phone is.
      const hits = await submit(phone);
      if (hits === null) {
        setMissingPrefix(true);
        return;
      }
      setMissingPrefix(false);
      setEligibility(recipientEligibility(hits, currentRecipientId));
    } catch {
      // The hook rethrows so the caller can tell a failed read from an empty one — «no account
      // with this phone» and «we could not ask» are different facts and owe the agent different
      // sentences. The error itself is not rendered: it is an untrusted string from the network
      // edge, and the panel owns every word here.
      setReadFailed(true);
      setEligibility(null);
    }
  };

  const handleResolve = () => {
    void resolve();
  };

  const chosen = eligibility?.eligible === true ? eligibility.person : null;
  // Only after a resolution: before the first search the field is empty and nothing has been
  // refused, so it carries no sentence and is not marked rejected.
  const refusal = eligibility?.eligible === false ? REFUSAL[eligibility.reason] : null;
  const searching = status === 'searching';
  // One slot, one sentence. The read's own troubles come first: until the panel has managed to ask,
  // it has nothing to say about any account.
  const helper = readFailed ? COPY.readFailed : missingPrefix ? COPY.missingPrefix : refusal;
  // The same verdict, spoken. The two are mutually exclusive by construction — a resolved account
  // clears every sentence under the field, and any sentence under the field means no account was
  // resolved — so this reads as one value and never stacks two. Empty until the agent has asked for
  // something, which is also what makes retyping fall silent rather than keep asserting a name they
  // have moved away from.
  const announced = chosen?.name ?? helper ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      label={COPY.title}
      // The dialog HAS a heading, so the heading names it and `label` stays the fallback.
      labelledBy={titleId}
      // A stray click outside must not discard a phone the agent is halfway through pasting.
      // Escape stays honoured: it is deliberate, a misplaced click is not.
      disableBackdropClose
      titleDivider
      title={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Text component="h2" variant="h6" id={titleId}>
            {COPY.title}
          </Text>
          {/* Pushes the «×» to the far edge, so the heading keeps the left margin the body is set
              to rather than being centred by a control that arrived after it. */}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton aria-label={COPY.close} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      }
      actions={
        <>
          <Button color="neutral" sentenceCase onClick={onClose}>
            {COPY.cancel}
          </Button>
          <Button
            variant="contained"
            sentenceCase
            loading={saving}
            disabled={chosen === null || saving}
            onClick={() => {
              if (chosen !== null) onSave(chosen);
            }}
          >
            {COPY.save}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {/* The magnifier sits OUTSIDE the field, as the design draws it — it is a control of its
            own and not an adornment, because it is what RUNS the search. Bottom-aligned so it
            lines up with the input and not with the label above it. */}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
          <TextField
            label={COPY.phoneLabel}
            placeholder={COPY.phonePlaceholder}
            value={phone}
            {...(helper !== null ? { error: true, helperText: helper } : {})}
            onChange={(next) => {
              setPhone(next);
              // Everything said under the field belonged to the number that produced it. Keeping
              // any of it while the agent retypes would leave a sentence under a phone it was
              // never about — and, worse, would leave «Guardar» live for an account they have
              // already moved away from.
              setEligibility(null);
              setReadFailed(false);
              setMissingPrefix(false);
            }}
            onKeyDown={(event) => {
              // Enter resolves too: the field has focus on mount, so requiring the pointer to
              // travel to the magnifier would undo what the autofocus just bought.
              if (event.key === 'Enter') handleResolve();
            }}
            autoFocus
            fullWidth
          />
          {/* `loading` disables it along with showing the indicator, which is the half that
              matters here: a second press must not fire a second search. */}
          <IconButton
            variant="outlined"
            aria-label={COPY.search}
            loading={searching}
            onClick={handleResolve}
          >
            <SearchIcon />
          </IconButton>
        </Stack>
        {/* An OUTPUT, not an input: it shows who the phone resolved to. Disabled rather than
            read-only because the design fills it grey — and because nothing here should offer to
            let an agent name the titular by hand. */}
        <TextField
          label={COPY.nameLabel}
          value={chosen?.name ?? ''}
          onChange={() => undefined}
          disabled
          fullWidth
        />
        {/* What «Guardar» is about to do that the agent cannot undo and nobody will be told about.
            It sits with the fields rather than behind a second step: pressing Guardar executes, so
            this is the last place it can still be read. `Text` + a glyph and NOT an `Alert` — an
            alert inside a dialog is a box in a box, and the sentence beside the icon carries the
            whole warning, so nothing is said by colour or glyph alone. */}
        <Stack spacing={0.5}>
          {authorizedCount > 0 && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <WarningIcon color="warning" fontSize="small" />
              <Text variant="body2" color="secondary">
                {COPY.authorizedWarning(authorizedCount)}
              </Text>
            </Stack>
          )}
          <Text variant="body2" color="secondary">
            {COPY.noNotice}
          </Text>
        </Stack>
      </Stack>
      {/* The verdict, for whoever cannot see it. The delivered design leaves the result in two
          places a live region cannot reach: on success the name is the VALUE of a disabled input —
          a form value is not a text node, so nothing announces it and nothing ever focuses it — and
          on refusal the sentence is MUI's helper text, reached only through `aria-describedby` on a
          field that already holds focus, which screen readers re-announce inconsistently. Without
          this the agent who cannot see the dialog reaches «Guardar» never having been told who the
          parcel is going to, which is the one thing they are here to check.

          `status`, not `alert`: it informs without interrupting — the agent asked for this answer.
          Mounted from the first render and left EMPTY rather than conditionally rendered, because a
          live region that arrives together with its text is announced unreliably. Clipped, never
          `display: none` or `visibility: hidden`, which would take it out of the accessibility tree
          and the announcement with it. Outside the `Stack` on purpose: Stack lays its spacing on
          every child after the first, and this one is not a row of the form. */}
      <Box
        role="status"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
      >
        {announced}
      </Box>
    </Dialog>
  );
}
